import { FormattedMessage } from 'react-intl';

import ChevronRightIcon from '@shieldpay/bumblebee/icons/ChevronRightIcon';
import CloseIcon from '@shieldpay/bumblebee/icons/CloseIcon';
import EmptyCheckIcon from '@shieldpay/bumblebee/icons/EmptyCheckIcon';
import FilledCheckIcon from '@shieldpay/bumblebee/icons/FilledCheckIcon';
import { DocumentUploaded, Monzo } from '@shieldpay/bumblebee/images';

import { Heading } from '@shieldpay/bumblebee/heading';
import { Column } from '@shieldpay/bumblebee/layout';
import { Link } from '@shieldpay/bumblebee/link';
import { List } from '@shieldpay/bumblebee/list';
import ListMarkdown from '@shieldpay/bumblebee/markdown/list.md';
import { Text } from '@shieldpay/bumblebee/text';

import { noop } from '@shieldpay/utility-functions-ui/noop';

import { ContentsTable } from '~/components/contents-table/contents-table';
import { Design } from '~/components/design/design';
import { MdxFormatter } from '~/components/mdx-formatter/mdx-formatter';
import { ComponentPreview } from '~/components/preview/component-preview';
import { createMeta } from '~/utilities/create-meta';

export const meta = createMeta({
  id: 'hive.components.list.title',
});

export const ListComponentPage = () => {
  return (
    <>
      <Heading>
        <FormattedMessage id="hive.components.list.title" />
      </Heading>

      <ContentsTable />

      <ComponentPreview alignContent={{ x: 'left' }}>
        <Column style={{ width: '440px' }} spacing="basePos5">
          <Column spacing="base">
            <Heading variant="headline2">Default</Heading>
            <List>
              <List.Item label="Label">Text Example</List.Item>
              <List.Item
                label="Label with is rather rather rather rather rather rather rather rather rather rather rather rather rather rather long"
                RightIcon={<ChevronRightIcon />}
              >
                Text Example with is rather rather rather rather rather rather
                rather rather rather rather rather rather rather rather long
              </List.Item>
              <List.Item LeftIcon={<EmptyCheckIcon />} label="Label">
                Text Example
              </List.Item>
            </List>
          </Column>
          <Column spacing="base">
            <Heading variant="headline2">Display</Heading>
            <List>
              <List.Item LeftIcon={<Monzo />} variant="display" label="Label">
                Text Example
              </List.Item>

              <List.Item
                LeftIcon={<DocumentUploaded />}
                variant="display"
                RightIcon={<ChevronRightIcon color="neutral250" />}
                label="Label which is rather rather rather rather rather rather rather rather long"
              >
                Text Example which is rather rather rather rather long
              </List.Item>

              <List.Item
                LeftIcon={<DocumentUploaded />}
                RightIcon={<CloseIcon color="neutral150" />}
                variant="display"
              >
                Document title
              </List.Item>
            </List>
          </Column>

          <Column spacing="base">
            <Heading variant="headline2">Uploader</Heading>
            <List>
              <List.Item
                variant="uploader"
                state="default"
                filename="filename.jpg"
                deleteDocumentText="delete"
                uploadingText="uploading"
                onClear={noop}
              />

              <List.Item
                variant="uploader"
                state="uploading"
                filename="filename.jpg"
                deleteDocumentText="delete"
                uploadingText="uploading"
                onClear={noop}
              />

              <List.Item
                variant="uploader"
                state="uploaded"
                filename="filename.jpg"
                deleteDocumentText="delete"
                uploadingText="uploading"
                onClear={noop}
              />
            </List>
          </Column>

          <Column spacing="base">
            <Heading variant="headline2">Timeline</Heading>
            <List>
              <List.Item
                LeftIcon={<FilledCheckIcon />}
                variant="timeline"
                position="first"
              >
                <Text>
                  You need to wrap text explicitly in the timeline variant
                </Text>
              </List.Item>

              <List.Item LeftIcon={<EmptyCheckIcon />} variant="timeline">
                <Text paragraph variant="bodyMedium150">
                  You need to wrap text explicitly
                </Text>
                <Text paragraph>
                  in the timeline variant because you can add as much text or
                  styling as you like
                </Text>
              </List.Item>
              <List.Item
                LeftIcon={<EmptyCheckIcon size="xl" color="success500" />}
                variant="timeline"
                RightIcon={<ChevronRightIcon color="success500" />}
              >
                <Text>
                  Lists are a continuous group of text or images. They are
                  composed of items which are represented by icons and text. The
                  list item component is used to display a list of user details.
                  Item 3 Lists are a continuous group of text or images. They
                  are composed of items which are represented by icons and text.
                  The list item component is used to display a list of user
                  details.
                </Text>
              </List.Item>
              <List.Item LeftIcon={<FilledCheckIcon />} variant="timeline">
                <Text>You could for example add a </Text>
                <Link href="#">link</Link>
              </List.Item>

              <List.Item
                LeftIcon={<EmptyCheckIcon />}
                variant="timeline"
                label="label"
              >
                <Text variant="body150">
                  There is also a label prop like other list items
                </Text>
              </List.Item>
            </List>
          </Column>
        </Column>
      </ComponentPreview>
      <Design>
        <Design.Section heading="Usage">
          <Text paragraph>
            Lists are a continuous group of text or images. They are composed of
            items which are represented by icons and text. The list item
            component is used to display a list of user details.
          </Text>
        </Design.Section>
        <Design.Section heading="Anatomy">
          <Text paragraph>
            The anatomy depends of the list item variant. There are 3 variants:
            default, display and timeline.
          </Text>
        </Design.Section>
        <Design.Section heading="Placement">
          <Text paragraph>
            This component sits in the review page on the onboarding flow and
            also as a way of displaying content inside the drawers.
          </Text>
        </Design.Section>

        <Design.Section heading="Accessibility">
          <Text paragraph>
            The ‘Text’ style colour used on entered text in the field, is
            comfortably within the AAA contrast ratio for legibility.
          </Text>
        </Design.Section>
      </Design>

      <MdxFormatter md={ListMarkdown} />
    </>
  );
};

export default ListComponentPage;
